import AsyncStorage from '@react-native-async-storage/async-storage';
import api from '@/api/axios';
import store from '@/stores/store';
import { localLoginUser } from '@/stores/auth/services';
import { startCheckConsent } from '@/utils/consents';
import { Platform } from 'react-native';
import { EpaExportResult, UserEpa } from '@/stores/store.types';
import { logError } from '@/utils/logHandling';
import { IS_IOS, IS_WEB } from '@/utils/platform';

const refreshUserEpa = async () => {
  try {
    const userEpa = await getEpaUser();
    store.getActions().setUserEpa(userEpa);
  } catch (error) {
    logError('Failed to refresh ePA user', error);
  }
};

export const writeToEpa = async (): Promise<EpaExportResult> => {
  try {
    const { data } = await api.post(
      '/api/patients/practicesessions/results/export/epa',
    );
    await refreshUserEpa();
    return data;
  } catch (error) {
    logError('Could not export results to ePA', error);
    return Promise.reject(error);
  }
};

export const deleteEpaLinked = async () => {
  try {
    return await api.delete('/api/users/epa');
  } catch (error) {
    logError('Could not delete ePA link', error);
    return Promise.reject(error);
  }
};

export const getEpaUser = async (): Promise<UserEpa> => {
  try {
    const { data } = await api.get('/api/users/epa');
    return data;
  } catch (error) {
    logError('Could not get ePA user', error);
    return Promise.reject(error);
  }
};

export const loginWithEpa = async (context: any, onError: () => void) => {
  const auth = async (authToken: string) => {
    const loginResponse = await loginWithIdpToken(authToken);
    if (
      loginResponse.error &&
      loginResponse.code === 'error.idp.noUserWithInsuranceIdFound'
    ) {
      onError();
      logError('No user with insurance ID found during ePA login');
    }
  };

  const { startAuth } = context;
  await startAuth(auth);
};

export const connectWithEpa = async (
  context: any,
  onSuccess: (insuranceId: string) => void,
  onError: () => void,
) => {
  try {
    const connect = async (authToken: string) => {
      try {
        const insuranceId = await connectWithIdp(authToken);
        await refreshUserEpa();
        onSuccess(insuranceId);
      } catch {
        onError();
        logError('Could not connect with ePA during ePA connection');
      }
    };

    const { startAuth } = context;
    await startAuth(connect);
  } catch {
    onError();
    logError('Could not start ePA connection');
  }
};

const connectWithIdp = async (token: string) => {
  try {
    const response = await api.post(
      `/api/auth/idp/connect-with-epa`,
      {
        token,
      },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );
    return await response.data.idpInsuranceId;
  } catch {
    logError('Could not connect with ePA during ePA connection');
    throw new Error('error.idp.idpConnectionFailed');
  }
};

const loginWithIdpToken = async (token: string) => {
  try {
    let deviceModel = IS_IOS
      ? 'ios-device'
      : (Platform.constants as any)?.Model;
    if (!deviceModel) {
      deviceModel = 'android-device';
    }
    const loginData = {
      app: IS_WEB ? 'browser' : Platform.OS,
      deviceModel,
    };

    const response = await api.post(
      `/api/auth/idp/login-with-idp`,
      {
        token,
        ...loginData,
      },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.data.accessToken) {
      logError('No access token created during ePA login');
      return {
        error: 'error.idp.noAccessTokenCreated',
      };
    }

    // Persist login to stay connected as if "stay logged in" was checked
    store.getActions().setStayLoggedIn(true);
    await AsyncStorage.setItem('stayLoggedIn', JSON.stringify(true));

    await localLoginUser({
      accessToken: response.data.accessToken,
      user: {
        _id: response.data.user._id,
        email: response.data.user.email,
        emailState: response.data.user.emailState,
        code: response.data.user.code,
        firstName: response.data.user.firstName,
        lastName: response.data.user.lastName,
        userEpa: response.data.user.userEpa,
        therapistCanSeeResults: response.data.user.therapistCanSeeResults,
        idpInsuranceId: response.data.user.idpInsuranceId,
        appLockPattern: response.data.user.appLockPattern,
        appStartResumeLock: response.data.user.appStartResumeLock,
        eigentrainingLock: response.data.user.eigentrainingLock,
        marketingChannelSelection: response.data.user.marketingChannelSelection,
      },
    });

    await refreshUserEpa();

    store
      .getActions()
      .initializeResultsWithKeyAndPracticeSessionWithKey(undefined);

    startCheckConsent();

    return {
      accessToken: response.data.accessToken,
    };
  } catch (e: any) {
    logError('Failed to login with IDP token', e);
    if (e.response?.data) {
      return e.response.data;
    }
  }
};
